import { maxBy, sum } from "lodash"

import React, { useEffect, useMemo, useState } from "react"

import { roundToColliSize } from "~/components/purchaseOrder/CreateOrUpdatePurchaseOrder"
import { Alert } from "~/components/reusable/Alert"
import Select from "~/components/reusable/Select"
import {
  useAddItemsToPurchaseOrder,
  useGetPurchaseOrder,
  useGetPurchaseOrders,
  useSuggestedPurchaseOrder,
} from "~/hooks/usePurchaseOrders"
import { trackEvent } from "~/hooks/useTrackingService"

import { Button } from "../../reusable/Button"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { usePrimaryWarehouses } from "~/hooks/useWarehouses"
import { filterNotUndefinedOrNull, largest, uniq } from "~/utils/tsUtils"
import { WarehouseSelector } from "~/components/purchaseOrder/selectors/WarehouseSelector"
import { VendorSelector } from "~/components/purchaseOrder/selectors/VendorSelector"
import { toast } from "react-toastify"
import { Checkbox } from "~/components/reusable/Checkbox"
import { Label } from "~/components/reusable/Label"
import { createPortal } from "react-dom"

type Props = {
  variants: IVariantSummary[]
  onEdit: () => void
  onCancel: () => void
  selectableVendors: { id: string; name: string }[]
  preSelectedVendorId?: string
  collectionIds?: string[]
}

const countVariantsWithSku = (variants: IVariantSummary[], match: (skuId: string) => boolean) =>
  variants.filter((v) => v.skuIds.some(match)).length

export default function EditPurchaseOrder({
  variants,
  onEdit,
  onCancel,
  selectableVendors,
  preSelectedVendorId,
}: Props) {
  const warehouses = usePrimaryWarehouses()
  const preferredVendorIds = uniq(
    filterNotUndefinedOrNull(variants.map((v) => v.settings.preferredVendorId)),
  )

  const [purchaseOrderId, setPurchaseOrderId] = React.useState<number | undefined>(undefined)
  const [onlySuggestedVariants, setOnlySuggestedVariants] = useState(false)

  const { data: purchaseOrders } = useGetPurchaseOrders("draft")
  const { data: selectedPurchaseOrder } = useGetPurchaseOrder(purchaseOrderId)
  const { mutate: addItems } = useAddItemsToPurchaseOrder()

  const selectedSkus = variants.flatMap((v) =>
    v.skuIds.map((skuId) => ({
      skuId,
      colliSize: v.settings.colliSize,
      totalLeadTimeDays: v.settings.totalLeadTimeDays,
      purchaseIntervalDays: v.settings.purchaseIntervalDays,
    })),
  )

  const defaultWarehouseIds = useMemo(() => {
    const warehouseId = maxBy(warehouses, (wh) =>
      sum(
        variants.flatMap((v) => v.stockOutInfoWarehouse[wh.id]?.map((s) => s.suggestedOrder ?? 0)),
      ),
    )?.id

    return warehouseId ? [warehouseId] : []
  }, [variants.length])

  const [selectedWarehouseIds, setSelectedWarehouseIds] = useState<string[]>(defaultWarehouseIds)
  const [vendorId, setVendorId] = useState<string>(
    preSelectedVendorId ??
      preferredVendorIds.find((id) => selectableVendors.some((v) => v.id === id)) ??
      selectableVendors[0]?.id ??
      "",
  )

  useEffect(() => {
    if (selectedWarehouseIds.length === 0 && warehouses?.length) {
      setSelectedWarehouseIds(warehouses[0]!.id ? [warehouses[0]!.id] : [])
    }
  }, [warehouses?.length])

  const items = useSuggestedPurchaseOrder(
    selectedSkus.map((sku) => sku.skuId),
    selectedWarehouseIds,
    selectedPurchaseOrder?.expectedArrivalDate,
    selectedPurchaseOrder?.purchasePeriodEndDate,
  )

  const colliSizes = useMemo(() => {
    const colliSizes: Map<string, number> = new Map()
    selectedSkus.forEach((sku) => {
      colliSizes.set(sku.skuId, sku.colliSize ?? 0)
    })
    return colliSizes
  }, [selectedSkus])

  const skuToVariantMap = useMemo(() => {
    const map = new Map<string, string>()
    variants.forEach((variant) => {
      variant.skuIds.forEach((skuId) => {
        map.set(skuId, variant.variantId)
      })
    })
    return map
  }, [variants])

  const variantsWithSuggestions = useMemo(() => {
    const variantSet = new Set<string>()
    items?.forEach((item) => {
      if (item.quantity > 0 && item.warehouseId === selectedWarehouseIds[0]) {
        const variantId = skuToVariantMap.get(item.skuId)
        if (variantId) {
          variantSet.add(variantId)
        }
      }
    })
    return variantSet
  }, [items, skuToVariantMap])

  useEffect(() => {
    if (
      purchaseOrderId &&
      purchaseOrders &&
      !purchaseOrders.find(
        (x) =>
          x.id === purchaseOrderId &&
          uniq(x.items.map((item) => item.warehouseId)).toString() ===
            selectedWarehouseIds.toString() &&
          x.vendorId === vendorId,
      )
    ) {
      setPurchaseOrderId(undefined)
    }
  }, [selectedWarehouseIds, vendorId])

  const poSkuIds = new Set(selectedPurchaseOrder?.items.map((item) => item.skuId))

  const skusWithSuggestedOrder = items?.filter((sku) => sku.quantity > 0) ?? []
  const suggestedSkuIds = new Set(skusWithSuggestedOrder.map((sku) => sku.skuId))

  const onSubmit = () => {
    if (!purchaseOrderId) return
    if (!items) return

    const updatedItems = items.filter((item) => {
      if (onlySuggestedVariants) {
        const variantId = skuToVariantMap.get(item.skuId)
        return variantId && variantsWithSuggestions.has(variantId)
      } else {
        return true
      }
    })

    const uniqueWarehouseIds = new Set(updatedItems.map((item) => item.warehouseId))

    if (uniqueWarehouseIds.size > 1) {
      toast.error("Cannot create purchase order with items from multiple warehouses")
      console.error(
        "Attempted to create PO with multiple warehouses:",
        Array.from(uniqueWarehouseIds),
      )
      return
    }

    // Fallback for variants with no forecast data (absent from ForecastVariantCompressed):
    // add all selected SKUs with quantity 0 so the user can set quantities manually in the PO editor.
    const itemsToAdd =
      updatedItems.length > 0 || onlySuggestedVariants
        ? updatedItems.map((item) => ({
            warehouseId: item.warehouseId,
            skuId: item.skuId,
            quantity: roundToColliSize(
              Math.ceil(largest(item.rawQuantity, 0) ?? 0),
              colliSizes.get(item.skuId) ?? 1,
            ),
          }))
        : selectedSkus
            .filter((sku) => !poSkuIds.has(sku.skuId))
            .map((sku) => ({
              warehouseId: selectedWarehouseIds[0]!,
              skuId: sku.skuId,
              quantity: 0,
            }))

    if (itemsToAdd.length === 0) {
      toast.warning("No items selected for purchase order")
      return
    }

    addItems(
      {
        purchaseOrderId,
        items: itemsToAdd,
      },
      {
        onSuccess: () => {
          trackEvent("Purchase Order Edited", {
            "Purchase Order Edit Source": "Dialog",
          })
          onEdit()
        },
      },
    )
  }

  const dialogFooterDomNode = document.getElementById("purchaseOrderDialogFooter")

  return (
    <div className="flex flex-col gap-4">
      <WarehouseSelector
        warehouses={warehouses}
        warehouseIds={selectedWarehouseIds}
        setWarehouseIds={setSelectedWarehouseIds}
      />
      <VendorSelector
        selectableVendors={selectableVendors}
        vendorId={vendorId}
        setVendorId={setVendorId}
        preferredVendorIds={preferredVendorIds}
      />

      <div className="flex flex-col gap-4">
        <Select
          key={purchaseOrderId}
          placeholder="Select a draft purchase order..."
          options={(purchaseOrders ?? [])
            .sort((a, b) => a.name.localeCompare(b.name))
            .filter(
              (o) =>
                uniq(o.items.map((item) => item.warehouseId)).toString() ===
                  selectedWarehouseIds.toString() && o.vendorId === vendorId,
            )}
          getOptionLabel={(po) => po.name}
          getOptionValue={(po) => po.id.toString()}
          onChange={(value) => setPurchaseOrderId(value?.id)}
          value={purchaseOrders?.find((x) => x.id === purchaseOrderId)}
          isDisabled={selectedWarehouseIds.length === 0 || vendorId === ""}
        />
        {selectedPurchaseOrder && (
          <Alert>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-sm mb-2">Summary of Selected Variants:</h4>
                <ul className="list-disc pl-5 space-y-2">
                  <li>
                    <span className="font-medium">Total selected:</span> {variants.length} variants
                    ({selectedSkus.length} SKUs)
                  </li>
                  <li>
                    <span className="font-medium">With a suggested order:</span>{" "}
                    {variantsWithSuggestions.size} variants{" "}
                    <i>
                      (
                      {sum(
                        skusWithSuggestedOrder.map((s) => Math.ceil(Math.max(s.rawQuantity, 0))),
                      )}{" "}
                      units)
                    </i>
                  </li>
                  <li>
                    <span className="font-medium">Already on purchase order:</span>{" "}
                    {countVariantsWithSku(variants, (skuId) => poSkuIds.has(skuId))} variants
                  </li>
                </ul>
              </div>
              <p>
                There are <b>{countVariantsWithSku(variants, (skuId) => !poSkuIds.has(skuId))}</b>{" "}
                variants left to be added. Out of these{" "}
                <b>
                  {countVariantsWithSku(
                    variants,
                    (skuId) => !poSkuIds.has(skuId) && suggestedSkuIds.has(skuId),
                  )}
                </b>{" "}
                have a suggested order. Do you wish to add all of them, or only the ones with a
                suggested order?
              </p>
            </div>
          </Alert>
        )}

        {!!dialogFooterDomNode &&
          createPortal(
            <div className="flex flex-row justify-between items-center w-full">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="only-suggested-variants-checkbox"
                  checked={onlySuggestedVariants}
                  onCheckedChange={() => setOnlySuggestedVariants(!onlySuggestedVariants)}
                />
                <Label className="cursor-pointer" htmlFor="only-suggested-variants-checkbox">
                  Only include variants with recommendations
                </Label>
              </div>
              <div className="flex gap-2">
                <Button variant="transparent" onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  requires="edit"
                  disabled={
                    !purchaseOrderId ||
                    (onlySuggestedVariants && !items?.some((s) => s.quantity > 0))
                  }
                  onClick={() => onSubmit()}
                >
                  Add to Order
                </Button>
              </div>
            </div>,
            dialogFooterDomNode,
          )}
      </div>
    </div>
  )
}
